import { useEffect, useRef, useState } from 'react';
import {
  App,
  Button,
  Drawer,
  Input,
  Spin,
  theme,
  Tooltip,
  Typography,
} from 'antd';
import { CloseOutlined, SendOutlined } from '@ant-design/icons';
import { useTranslation } from 'react-i18next';
import { API } from '@/services/api.service';
import { useAuthStore } from '@/store/useAuthStore';
import MessageBubble from './MessageBubble';
import MessageContent from './MessageContent';
import CitationChips from './CitationChips';
import DocumentViewer from './DocumentViewer';
import RobotAccountantAvatar from './RobotAccountantAvatar';

const { Text } = Typography;

const SUPPORTED_LANGUAGES = ['sr', 'en', 'ru', 'sq', 'pl'];

// Floating launcher button size and where its position is remembered.
const BTN_SIZE = 72;
const POS_KEY = 'hlb-assistant-btn-pos';
const EDGE = 16;

const clampPos = ({ left, top }) => ({
  left: Math.min(Math.max(EDGE, left), window.innerWidth - BTN_SIZE - EDGE),
  top: Math.min(Math.max(EDGE, top), window.innerHeight - BTN_SIZE - EDGE),
});

const defaultPos = () => ({
  left: window.innerWidth - BTN_SIZE - 24,
  top: window.innerHeight - BTN_SIZE - 24,
});

const AssistantWidget = () => {
  const { t, i18n } = useTranslation('assistant');
  const { message } = App.useApp();
  const { token } = theme.useToken();
  const user = useAuthStore((s) => s.user);

  // Global config from Tech Admin (visibility + persona). Null until loaded.
  const [config, setConfig] = useState(null);
  const [configLoaded, setConfigLoaded] = useState(false);

  const resolveLang = () => {
    const [lang] = (i18n.language || 'sr').split('-');
    return SUPPORTED_LANGUAGES.includes(lang) ? lang : 'sr';
  };

  // Persona overrides fall back to the i18n copy when unset.
  const botName = config?.persona?.botName || t('widget.botName');
  const greetingText =
    config?.persona?.greeting?.[resolveLang()] || t('widget.greeting');

  // Greeting with the bot name rendered uppercase in the theme's primary color.
  const renderGreeting = () => {
    const parts = greetingText.split(botName);
    if (parts.length === 1) return greetingText;
    return parts.flatMap((part, i) =>
      i === 0
        ? [part]
        : [
            <span
              key={`n${i}`}
              style={{ color: token.colorPrimary, fontWeight: 700 }}
            >
              {botName.toUpperCase()}
            </span>,
            part,
          ]
    );
  };

  const [open, setOpen] = useState(false);
  const [messages, setMessages] = useState([]);
  const [conversationId, setConversationId] = useState(null);
  const [input, setInput] = useState('');
  const [sending, setSending] = useState(false);
  const [escalatingId, setEscalatingId] = useState(null);
  const [viewer, setViewer] = useState({
    open: false,
    documentId: null,
    chunkId: null,
  });

  const openSource = (citation) => {
    if (!citation?.documentId) return;
    setViewer({
      open: true,
      documentId: citation.documentId,
      chunkId: citation.chunkId || null,
    });
  };

  // Draggable launcher button position (persisted across sessions).
  const [pos, setPos] = useState(null);
  const [dragging, setDragging] = useState(false);
  const dragRef = useRef({ active: false, moved: false, dx: 0, dy: 0, sx: 0, sy: 0 });

  // Guards against the mobile "ghost click": the tap that opens the drawer is
  // re-dispatched as a click onto the freshly-mounted mask, which would close it.
  const openedAtRef = useRef(0);
  const openWidget = () => {
    openedAtRef.current = Date.now();
    setOpen(true);
  };
  const handleDrawerClose = () => {
    if (Date.now() - openedAtRef.current < 400) return;
    setOpen(false);
  };

  useEffect(() => {
    let initial = null;
    try {
      const saved = JSON.parse(localStorage.getItem(POS_KEY) || 'null');
      if (saved && Number.isFinite(saved.left) && Number.isFinite(saved.top)) {
        initial = clampPos(saved);
      }
    } catch {
      /* ignore */
    }
    setPos(initial || defaultPos());
  }, []);

  useEffect(() => {
    const onResize = () => setPos((p) => (p ? clampPos(p) : p));
    window.addEventListener('resize', onResize);
    return () => window.removeEventListener('resize', onResize);
  }, []);

  const handleBtnPointerDown = (e) => {
    const rect = e.currentTarget.getBoundingClientRect();
    dragRef.current = {
      active: true,
      moved: false,
      dx: e.clientX - rect.left,
      dy: e.clientY - rect.top,
      sx: e.clientX,
      sy: e.clientY,
    };
    e.currentTarget.setPointerCapture?.(e.pointerId);
  };

  const handleBtnPointerMove = (e) => {
    const d = dragRef.current;
    if (!d.active) return;
    if (!d.moved && Math.hypot(e.clientX - d.sx, e.clientY - d.sy) > 5) {
      d.moved = true;
      setDragging(true);
    }
    if (d.moved) {
      setPos(clampPos({ left: e.clientX - d.dx, top: e.clientY - d.dy }));
    }
  };

  const handleBtnPointerUp = (e) => {
    const d = dragRef.current;
    d.active = false;
    e.currentTarget.releasePointerCapture?.(e.pointerId);
    if (d.moved) {
      setDragging(false);
      setPos((p) => {
        if (p) {
          try {
            localStorage.setItem(POS_KEY, JSON.stringify(p));
          } catch {
            /* ignore */
          }
        }
        return p;
      });
    } else {
      openWidget();
    }
  };

  const abortRef = useRef(null);
  const idRef = useRef(0);
  const listEndRef = useRef(null);

  const nextId = () => {
    idRef.current += 1;
    return idRef.current;
  };

  // Auto-scroll to the bottom whenever content changes.
  useEffect(() => {
    listEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages, open]);

  // Abort any in-flight request on unmount.
  useEffect(() => {
    return () => abortRef.current?.abort();
  }, []);

  // Load the widget's public config once (visibility + persona).
  useEffect(() => {
    if (!user) return;
    let cancelled = false;
    API.assistant
      .getPublicConfig()
      .then((res) => {
        if (!cancelled) setConfig(res?.data || null);
      })
      .catch(() => {
        /* fall back to i18n persona / default visibility on failure */
      })
      .finally(() => {
        if (!cancelled) setConfigLoaded(true);
      });
    return () => {
      cancelled = true;
    };
  }, [user]);

  const language = (() => {
    const [lang] = (i18n.language || 'sr').split('-');
    return SUPPORTED_LANGUAGES.includes(lang) ? lang : 'sr';
  })();

  const patchMessage = (id, patch) => {
    setMessages((prev) =>
      prev.map((m) =>
        m.id === id ? { ...m, ...(typeof patch === 'function' ? patch(m) : patch) } : m
      )
    );
  };

  const handleNewChat = () => {
    abortRef.current?.abort();
    abortRef.current = null;
    setConversationId(null);
    setMessages([]);
    setInput('');
    setSending(false);
  };

  const handleSend = async () => {
    const text = input.trim();
    if (!text || sending) return;

    // Abort any previous in-flight request before starting a new one.
    abortRef.current?.abort();
    const controller = new AbortController();
    abortRef.current = controller;

    const userMessage = { id: nextId(), role: 'user', content: text };
    const assistantId = nextId();
    const assistantMessage = {
      id: assistantId,
      role: 'assistant',
      content: '',
      streaming: true,
      question: text,
      citations: [],
      outOfScope: false,
    };

    setMessages((prev) => [...prev, userMessage, assistantMessage]);
    setInput('');
    setSending(true);

    await API.assistant.streamMessage({
      conversationId: conversationId || undefined,
      message: text,
      language,
      signal: controller.signal,
      onDelta: (chunk) => {
        patchMessage(assistantId, (m) => ({ content: m.content + (chunk || '') }));
      },
      onDone: ({ conversationId: newId, citations, outOfScope } = {}) => {
        if (newId) setConversationId(newId);
        patchMessage(assistantId, {
          streaming: false,
          citations: citations || [],
          outOfScope: !!outOfScope,
        });
      },
      onError: ({ message: errMessage } = {}) => {
        patchMessage(assistantId, (m) => ({
          streaming: false,
          isError: true,
          content: m.content || t('widget.error'),
        }));
        if (errMessage) message.error(t('widget.error'));
      },
    });

    if (abortRef.current === controller) abortRef.current = null;
    setSending(false);
  };

  const handleKeyDown = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSend();
    }
  };

  const handleEscalate = async (msg) => {
    setEscalatingId(msg.id);
    try {
      const res = await API.assistant.escalateToSupport({ message: msg.question });
      message.success(t('widget.escalated', { number: res.ticketNumber }));
      patchMessage(msg.id, { outOfScope: false, escalated: true });
    } catch {
      message.error(t('widget.escalateFailed'));
    } finally {
      setEscalatingId(null);
    }
  };

  if (!user) return null;

  // Per-company premium gate (mirrors hasTools/hasCalculator).
  if (user.hasAssistant === false) return null;

  // Wait for config so the widget doesn't flash before a kill-switch/role check.
  if (!configLoaded) return null;

  if (config) {
    if (config.enabled === false) return null;
    const roles = user.roles || [];
    const visibleRoles = config.visibleRoles || [];
    if (visibleRoles.length && !roles.some((r) => visibleRoles.includes(r))) {
      return null;
    }
  }

  const showGreeting = messages.length === 0;

  return (
    <>
      {pos && (
        <div
          className="fixed z-50"
          style={{ left: pos.left, top: pos.top, touchAction: 'none' }}
        >
          <Tooltip
            title={t('widget.tooltip')}
            placement="left"
            open={dragging ? false : undefined}
          >
            <button
              type="button"
              className={`hlb-bot-button${dragging ? ' hlb-bot-dragging' : ''}`}
              onPointerDown={handleBtnPointerDown}
              onPointerMove={handleBtnPointerMove}
              onPointerUp={handleBtnPointerUp}
              onKeyDown={(e) => {
                if (e.key === 'Enter' || e.key === ' ') {
                  e.preventDefault();
                  openWidget();
                }
              }}
              aria-label={botName}
            >
              <span className="hlb-bot-float">
                <RobotAccountantAvatar size={52} />
              </span>
            </button>
          </Tooltip>
        </div>
      )}

      <Drawer
        title={
          <div
            style={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              width: '100%',
            }}
          >
            <span style={{ display: 'inline-flex', alignItems: 'center', gap: 8 }}>
              <Button
                type="text"
                size="small"
                aria-label={t('widget.close')}
                icon={<CloseOutlined />}
                onClick={() => setOpen(false)}
              />
              <RobotAccountantAvatar size={28} animated={false} />
              <span>{t('widget.title')}</span>
            </span>
            <Button type="text" size="small" onClick={handleNewChat}>
              {t('widget.newChat')}
            </Button>
          </div>
        }
        placement="right"
        width="min(420px, 100vw)"
        open={open}
        onClose={handleDrawerClose}
        closable={false}
        styles={{
          header: { borderBottom: `1px solid ${token.colorPrimary}` },
          body: { display: 'flex', flexDirection: 'column', padding: 0 },
        }}
      >
        <div className="flex-1 overflow-y-auto" style={{ padding: 16 }}>
          {showGreeting && (
            <MessageBubble role="assistant">{renderGreeting()}</MessageBubble>
          )}

          {messages.map((msg) => (
            <div key={msg.id}>
              <MessageBubble role={msg.role}>
                {msg.role === 'assistant' && msg.streaming && !msg.content ? (
                  <span className="inline-flex items-center gap-2">
                    <Spin size="small" />
                    <Text type="secondary">{t('widget.thinking')}</Text>
                  </span>
                ) : msg.role === 'assistant' && !msg.streaming ? (
                  <MessageContent
                    content={msg.content}
                    citations={msg.citations}
                    onOpenSource={openSource}
                  />
                ) : (
                  msg.content
                )}
              </MessageBubble>

              {msg.role === 'assistant' && !msg.streaming && (
                <div style={{ marginLeft: 4, marginBottom: 8 }}>
                  <CitationChips
                    label={t('widget.sources')}
                    citations={msg.citations}
                    onOpenSource={openSource}
                  />
                  {msg.outOfScope && !msg.escalated && (
                    <Button
                      size="small"
                      style={{ marginTop: 6 }}
                      loading={escalatingId === msg.id}
                      onClick={() => handleEscalate(msg)}
                    >
                      {t('widget.escalate')}
                    </Button>
                  )}
                </div>
              )}
            </div>
          ))}

          <div ref={listEndRef} />
        </div>

        <div
          className="flex items-end gap-2"
          style={{
            padding: 12,
            borderTop: `1px solid ${token.colorPrimary}`,
          }}
        >
          <Input.TextArea
            value={input}
            onChange={(e) => setInput(e.target.value)}
            onKeyDown={handleKeyDown}
            placeholder={t('widget.placeholder')}
            autoSize={{ minRows: 1, maxRows: 4 }}
            style={{ flex: 1 }}
          />
          <Button
            type="primary"
            icon={<SendOutlined />}
            loading={sending}
            disabled={!input.trim()}
            onClick={handleSend}
          >
            {t('widget.send')}
          </Button>
        </div>
      </Drawer>

      <DocumentViewer
        open={viewer.open}
        documentId={viewer.documentId}
        chunkId={viewer.chunkId}
        onClose={() => setViewer((v) => ({ ...v, open: false }))}
      />
    </>
  );
};

export default AssistantWidget;
